/**
 * A company's own careers page: its short page name ("slug", arevina.com/jobs/<slug>) and the
 * optional branding an owner sets in "Careers page" settings (src/pages/employer/EmployerCareersPage.tsx).
 *
 * Shared, pure (no Firebase, no DOM): the browser uses it for the live preview and the form checks;
 * the server (functions/src/careersPage.ts, via scripts/syncFunctionsGenerated.mjs) uses the SAME
 * rules to validate before it claims a slug in a transaction. scripts/qa/offline-careers-slug.mjs tests it.
 *
 * Where things live:
 *   organizations/{orgId}.careersPage   { slug, headline, intro, highlights[], benefits[], contactLine,
 *                                         website, shareToLinkedIn, linkedInCompanyId, updatedAt, updatedBy }
 *                                         (server writes only)
 *   organizations/{orgId}.brandColor    the accent colour (already existed; client-writable)
 *   organizations/{orgId}.logoURL / bannerURL   logo and cover photo (existing uploads)
 *   careerSlugs/{slug}                  { orgId, active, createdAt, replacedAt? }  (public get, server writes only)
 *                                       an old slug stays with active:false so old links keep redirecting.
 *
 * /jobs/<x> is a company page when <x> is an active slug and a job page otherwise. Slugs can never
 * collide with a job: they never have the shape of an auto-generated job ID, can never be a word the
 * app uses under /jobs, never start with the prefixes our scripts use for hand-made job IDs, and the
 * server refuses any slug that is an existing job's ID.
 */

export const SLUG_MIN = 3;
export const SLUG_MAX = 40;
export const MAX_HIGHLIGHTS = 6;
export const MAX_BENEFITS = 12;
export const HEADLINE_MAX = 90;
export const INTRO_MAX = 400;
export const HIGHLIGHT_TITLE_MAX = 40;
export const HIGHLIGHT_TEXT_MAX = 120;
export const BENEFIT_MAX = 140;
export const CONTACT_MAX = 160;
/** LinkedIn company IDs are numbers; 20 digits is far more than any real one. */
export const LINKEDIN_COMPANY_ID_MAX = 20;
const LINKEDIN_COMPANY_ID_RE = /^\d{1,20}$/;

/** Lowercase letters, numbers and single hyphens; starts and ends with a letter or number. */
const SLUG_RE = /^[a-z0-9](?:[a-z0-9]|-(?!-))*[a-z0-9]$/;

/**
 * Words that are (or may become) app addresses under /jobs, or would confuse visitors. Includes
 * every static segment App.tsx and the server use under /jobs today: "companies" (the EMT company
 * map) and "apply" (/jobs/:jobId/apply), plus common words reserved for future pages.
 */
export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  'companies', 'company', 'apply', 'new', 'search', 'edit', 'create', 'post', 'posts', 'saved', 'save',
  'map', 'list', 'all', 'board', 'boards', 'careers', 'career', 'employer', 'employers', 'jobs', 'job',
  'admin', 'api', 'login', 'signin', 'signup', 'register', 'about', 'help', 'support', 'terms', 'privacy',
  'emergency', 'browse', 'index', 'home', 'recommended', 'matches', 'applications', 'application',
  'sitemap', 'rss', 'feed', 'feeds', 'linkedin', 'google', 'indeed', 'arevina', 'readyresponder',
  'share', 'preview', 'settings', 'account', 'dashboard', 'open', 'closed', 'draft', 'drafts',
  'import', 'export', 'null', 'undefined', 'nan', 'www', 'mail', 'static', 'assets', 'public',
]);

/** Prefixes of job IDs our scripts create by hand (seed-job-..., item2-qa-job-..., testemp-...). */
const RESERVED_PREFIXES = ['seed-job', 'testemp', 'qa-', 'test-job'];

/** The shape of a Firestore auto-generated document ID: 20 letters/digits. */
export function looksLikeJobId(value: string): boolean {
  if (/^[A-Za-z0-9]{20}$/.test(value)) return true;
  if (/^item\d+-qa-/.test(value)) return true;
  return RESERVED_PREFIXES.some((p) => value.startsWith(p));
}

export type SlugProblem = 'empty' | 'too_short' | 'too_long' | 'characters' | 'reserved' | 'looks_like_job';

export const SLUG_PROBLEM_TEXT: Record<SlugProblem, string> = {
  empty: 'Choose a page name.',
  too_short: `Use at least ${SLUG_MIN} characters.`,
  too_long: `Use ${SLUG_MAX} characters or fewer.`,
  characters: 'Use lowercase letters, numbers and single hyphens only, starting and ending with a letter or number.',
  reserved: 'That word is used by Arevina. Choose another page name.',
  looks_like_job: 'That looks like a job link. Choose a name with words, for example your company name.',
};

/** Why a slug can't be used (format only; availability is checked on the server), or null. */
export function slugProblem(raw: string): SlugProblem | null {
  const s = raw ?? '';
  if (!s) return 'empty';
  if (s.length < SLUG_MIN) return 'too_short';
  if (s.length > SLUG_MAX) return 'too_long';
  if (!SLUG_RE.test(s)) return 'characters';
  if (RESERVED_SLUGS.has(s)) return 'reserved';
  if (looksLikeJobId(s)) return 'looks_like_job';
  return null;
}

export function isValidSlug(raw: string): boolean {
  return slugProblem(raw) === null;
}

/**
 * Could /jobs/<x> be a company page? Only slug-shaped values are looked up; anything else (a job's
 * mixed-case auto ID, "companies") goes straight to the job page with no extra read.
 */
export function couldBeCareersSlug(x: string): boolean {
  return isValidSlug(x);
}

/** What the user typed, made into a slug as they type (lowercase, spaces to hyphens, no doubles). */
export function normalizeSlugInput(raw: string): string {
  return (raw || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-/, '')
    .slice(0, SLUG_MAX);
}

const COMPANY_SUFFIXES = /\b(llc|l\.l\.c\.|inc|inc\.|incorporated|corp|corporation|co|company|ltd|pllc|pc|lp|llp)\b\.?/gi;

/** A suggested slug from the company name ("MedEx Medical Transport, LLC" -> "medex-medical-transport"). */
export function suggestSlug(companyName: string): string {
  const base = normalizeSlugInput((companyName || '').replace(COMPANY_SUFFIXES, ' ')).replace(/-+$/, '');
  let s = base.slice(0, SLUG_MAX).replace(/-+$/, '');
  if (s.length < SLUG_MIN) s = `${s ? `${s}-` : ''}careers`;
  if (slugProblem(s)) {
    const withJobs = `${s.slice(0, SLUG_MAX - 5).replace(/-+$/, '')}-jobs`;
    if (!slugProblem(withJobs)) return withJobs;
    return 'my-company-jobs';
  }
  return s;
}

/** Alternatives to offer when a slug is taken. */
export function slugAlternatives(slug: string, cityOrState?: string): string[] {
  const out: string[] = [];
  const trim = (s: string) => s.slice(0, SLUG_MAX).replace(/-+$/, '');
  const place = normalizeSlugInput(cityOrState || '');
  if (place) out.push(trim(`${slug}-${place}`));
  out.push(trim(`${slug}-jobs`), trim(`${slug}-careers`));
  return Array.from(new Set(out)).filter((s) => s !== slug && isValidSlug(s));
}

// ─── Settings ───────────────────────────────────────────────────────────────

export type CareersHighlight = { title: string; text: string };

export type CareersPageSettings = {
  slug: string;
  headline: string;
  intro: string;
  highlights: CareersHighlight[];
  benefits: string[];
  contactLine: string;
  website: string;
  /** "Share my jobs to LinkedIn" (phase 2 feed). On unless the owner turns it off. */
  shareToLinkedIn: boolean;
  /** The company's LinkedIn company ID (digits), sent as <companyId> in the LinkedIn feed. Optional. */
  linkedInCompanyId: string;
};

export const EMPTY_CAREERS_SETTINGS: CareersPageSettings = {
  slug: '',
  headline: '',
  intro: '',
  highlights: [],
  benefits: [],
  contactLine: '',
  website: '',
  shareToLinkedIn: true,
  linkedInCompanyId: '',
};

type AnyRecord = Record<string, unknown>;
const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const oneLine = (s: string) => s.replace(/\s+/g, ' ').trim();

/** The saved settings of an org (missing fields filled with empties). */
export function readCareersSettings(org: AnyRecord | null | undefined): CareersPageSettings {
  const c = (org?.careersPage ?? null) as AnyRecord | null;
  if (!c || typeof c !== 'object') return { ...EMPTY_CAREERS_SETTINGS };
  const highlights = Array.isArray(c.highlights)
    ? (c.highlights as unknown[])
        .map((h) => (h && typeof h === 'object' ? { title: str((h as AnyRecord).title), text: str((h as AnyRecord).text) } : null))
        .filter((h): h is CareersHighlight => !!h && !!(h.title || h.text))
    : [];
  const benefits = Array.isArray(c.benefits) ? (c.benefits as unknown[]).map(str).filter(Boolean) : [];
  return {
    slug: str(c.slug),
    headline: str(c.headline),
    intro: str(c.intro),
    highlights,
    benefits,
    contactLine: str(c.contactLine),
    website: str(c.website),
    shareToLinkedIn: c.shareToLinkedIn !== false,
    linkedInCompanyId: LINKEDIN_COMPANY_ID_RE.test(str(c.linkedInCompanyId)) ? str(c.linkedInCompanyId) : '',
  };
}

/** The org's active careers slug, or '' when it has none. */
export function orgCareersSlug(org: AnyRecord | null | undefined): string {
  const s = readCareersSettings(org).slug;
  return isValidSlug(s) ? s : '';
}

/** Does this org have its own branded careers page (a saved slug)? */
export function hasCareersPage(org: AnyRecord | null | undefined): boolean {
  return !!orgCareersSlug(org);
}

export type SettingsProblem = { field: string; message: string };

function cleanUrl(raw: string): string | null {
  const s = raw.trim();
  if (!s) return '';
  const withScheme = /^https?:\/\//i.test(s) ? s : `https://${s}`;
  try {
    const u = new URL(withScheme);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
    if (!u.hostname.includes('.')) return null;
    return u.toString().replace(/\/$/, u.pathname === '/' ? '' : '/');
  } catch {
    return null;
  }
}

/**
 * Clean and check settings coming from the form (browser) or the callable (server). Text is
 * trimmed to one line (the intro keeps line breaks), empty highlights and benefits are dropped,
 * em dashes become plain hyphens with spaces (house style: no em dashes on user-facing pages).
 */
export function sanitizeCareersSettings(input: unknown): { value: CareersPageSettings; problems: SettingsProblem[] } {
  const raw = (input && typeof input === 'object' ? input : {}) as AnyRecord;
  const problems: SettingsProblem[] = [];
  const noEmDash = (s: string) => s.replace(/\s*\u2014\s*/g, ', ');
  const text = (v: unknown, max: number, field: string, keepBreaks = false): string => {
    let s = typeof v === 'string' ? v : '';
    s = keepBreaks ? s.replace(/\r\n/g, '\n').replace(/[ \t]+/g, ' ').replace(/\n{3,}/g, '\n\n').trim() : oneLine(s);
    s = noEmDash(s);
    if (s.length > max) problems.push({ field, message: `Keep this to ${max} characters or fewer.` });
    return s.slice(0, max);
  };

  const slug = typeof raw.slug === 'string' ? raw.slug.trim() : '';
  const sp = slugProblem(slug);
  if (sp) problems.push({ field: 'slug', message: SLUG_PROBLEM_TEXT[sp] });

  const highlightsIn = Array.isArray(raw.highlights) ? raw.highlights : [];
  const highlights: CareersHighlight[] = [];
  highlightsIn.forEach((h, i) => {
    const r = (h && typeof h === 'object' ? h : {}) as AnyRecord;
    const title = text(r.title, HIGHLIGHT_TITLE_MAX, `highlights.${i}.title`);
    const body = text(r.text, HIGHLIGHT_TEXT_MAX, `highlights.${i}.text`);
    if (title || body) highlights.push({ title, text: body });
  });
  if (highlights.length > MAX_HIGHLIGHTS) problems.push({ field: 'highlights', message: `Up to ${MAX_HIGHLIGHTS} highlights.` });

  const benefitsIn = Array.isArray(raw.benefits) ? raw.benefits : [];
  const benefits = benefitsIn.map((b, i) => text(b, BENEFIT_MAX, `benefits.${i}`)).filter(Boolean);
  if (benefits.length > MAX_BENEFITS) problems.push({ field: 'benefits', message: `Up to ${MAX_BENEFITS} benefits.` });

  const websiteRaw = typeof raw.website === 'string' ? raw.website : '';
  const website = cleanUrl(websiteRaw);
  if (website === null) problems.push({ field: 'website', message: 'Enter a full web address, for example https://www.example.com' });

  const linkedInCompanyId = typeof raw.linkedInCompanyId === 'string' ? raw.linkedInCompanyId.trim() : '';
  if (linkedInCompanyId && !LINKEDIN_COMPANY_ID_RE.test(linkedInCompanyId)) {
    problems.push({ field: 'linkedInCompanyId', message: 'Use numbers only, or leave it empty.' });
  }

  return {
    value: {
      slug,
      headline: text(raw.headline, HEADLINE_MAX, 'headline'),
      intro: text(raw.intro, INTRO_MAX, 'intro', true),
      highlights: highlights.slice(0, MAX_HIGHLIGHTS),
      benefits: benefits.slice(0, MAX_BENEFITS),
      contactLine: text(raw.contactLine, CONTACT_MAX, 'contactLine'),
      website: website ?? '',
      shareToLinkedIn: raw.shareToLinkedIn !== false,
      linkedInCompanyId: LINKEDIN_COMPANY_ID_RE.test(linkedInCompanyId) ? linkedInCompanyId : '',
    },
    problems,
  };
}

/**
 * What the branded page shows, with the company profile filling every gap so the page looks
 * complete even when the owner only chose a slug. Every claim comes from the company's own fields.
 */
export function careersPageContent(org: AnyRecord | null | undefined) {
  const s = readCareersSettings(org);
  const name = str(org?.name) || 'Open jobs';
  const about = str(org?.description) || str(org?.mission);
  const orgBenefits = Array.isArray(org?.benefits)
    ? (org!.benefits as unknown[]).map(str).filter(Boolean)
    : str(org?.benefits)
      ? str(org?.benefits).split(/\n+/).map((b) => b.replace(/^[-*\u2022]\s*/, '').trim()).filter(Boolean)
      : [];
  return {
    name,
    slug: isValidSlug(s.slug) ? s.slug : '',
    headline: s.headline || `Careers at ${name}`,
    intro: s.intro || about,
    highlights: s.highlights,
    benefits: s.benefits.length ? s.benefits : orgBenefits,
    contactLine: s.contactLine,
    website: s.website || str(org?.website),
    logoURL: str(org?.logoURL),
    coverURL: str(org?.bannerURL),
    shareToLinkedIn: s.shareToLinkedIn,
  };
}

/** /jobs/<slug> */
export const careersSlugPath = (slug: string): string => `/jobs/${slug}`;
